/**
 * @file dateRanges.ts
 * @brief Shared date-range helpers for dashboard KPI queries.
 *
 * @details
 * Centralises "start of today/month" and "end of now" so Super Admin
 * and Company Admin dashboards stay aligned on what "today" and "MTD"
 * mean. Related: features/dashboard/*Page.tsx, features/analytics/api.ts.
 *
 * @author DES
 * @date 07-06-2026
 */

// ----------------------------------------------------------------------
// Range builders — used by dashboard TanStack Query hooks
// ----------------------------------------------------------------------

export function startOfToday(): Date {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}

export function startOfMonth(): Date {
  const d = new Date();
  d.setDate(1);
  d.setHours(0, 0, 0, 0);
  return d;
}

export function endOfNow(): Date {
  const d = new Date();
  d.setHours(23, 59, 59, 999);
  return d;
}

export function monthStartIso(): string {
  return startOfMonth().toISOString();
}

export function todayStartIso(): string {
  return startOfToday().toISOString();
}

export function nowEndIso(): string {
  return endOfNow().toISOString();
}

// (24-08-2026) `monthStartYmd` / `todayYmd` were removed. They produced bare
// 'YYYY-MM-DD', which the invoices backend reads as UTC midnight — so the one
// query using them (the company open balance) framed a different window than
// every sibling card on the same dashboard, which all send local-day ISO.
// Send `monthStartIso()` / `nowEndIso()`; if a YMD is ever genuinely needed,
// pass a `tz` alongside it so the server buckets on the same calendar.

/** Last 30 days through end of today — used by dashboard trend charts. */
export function last30DaysTrendRange() {
  const end = endOfNow();
  const start = new Date(end);
  start.setDate(start.getDate() - 29);
  start.setHours(0, 0, 0, 0);
  return {
    startDate: start.toISOString(),
    endDate: end.toISOString(),
    groupBy: "day" as const,
  };
}

export function last30DaysBaseRange() {
  const { startDate, endDate } = last30DaysTrendRange();
  return { startDate, endDate };
}
